import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent } from '@mui/material';

interface RadarChartDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

const categoryNames: Record<string, { hebrew: string; english: string }> = {
  'management': { hebrew: 'ניהול', english: 'Management' },
  'organization': { hebrew: 'ארגון', english: 'Organization' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
  'finance': { hebrew: 'כספים', english: 'Finance' },
  'operations': { hebrew: 'תפעול', english: 'Operations' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
  'stress': { hebrew: 'מתח', english: 'Stress' },
  'principles': { hebrew: 'עקרונות', english: 'Principles' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
};

const RadarChartDashboard: React.FC<RadarChartDashboardProps> = ({ categoryScores, language }) => {
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
    };
  }, [categoryScores, language]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  const drawRadarChart = (scores: any[], centerX: number, centerY: number, radius: number) => {
    const numPoints = scores.length;
    const angleStep = (2 * Math.PI) / numPoints;

    // Create grid circles (20%, 40%, 60%, 80%, 100%)
    const gridCircles = [20, 40, 60, 80, 100].map(percent => {
      const r = (radius * percent) / 100;
      return `<circle cx="${centerX}" cy="${centerY}" r="${r}" fill="none" stroke="#e0e0e0" stroke-width="1"/>`;
    }).join('');

    // Create axis lines and labels
    const axisLines = scores.map((score, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);

      const labelX = centerX + (radius + 25) * Math.cos(angle);
      const labelY = centerY + (radius + 25) * Math.sin(angle);

      return `
        <line x1="${centerX}" y1="${centerY}" x2="${x}" y2="${y}" stroke="#ddd" stroke-width="1"/>
        <text x="${labelX}" y="${labelY}" text-anchor="middle" font-size="11" fill="#333" font-weight="600">${score.name}</text>
      `;
    }).join('');

    // Create data polygon
    const points = scores.map((score, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const r = (radius * score.value) / 100;
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      return `${x},${y}`;
    }).join(' ');

    // Create data points
    const dataPoints = scores.map((score, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const r = (radius * score.value) / 100;
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      return `<circle cx="${x}" cy="${y}" r="4" fill="${getScoreColor(score.value)}" stroke="white" stroke-width="2"/>`;
    }).join('');

    return `
      ${gridCircles}
      ${axisLines}
      <polygon points="${points}" fill="rgba(52, 152, 219, 0.3)" stroke="#3498db" stroke-width="2"/>
      ${dataPoints}
      <text x="${centerX}" y="${centerY - 5}" text-anchor="middle" font-size="14" font-weight="bold" fill="#3498db">
        ${scores.reduce((sum, s) => sum + s.value, 0) / scores.length | 0}%
      </text>
      <text x="${centerX}" y="${centerY + 10}" text-anchor="middle" font-size="10" fill="#666">
        Average
      </text>
    `;
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
        {language === 'he' ? '📊 ניתוח רדאר מקיף' : '📊 Comprehensive Radar Analysis'}
      </Typography>

      <Grid container spacing={3}>
        {/* Business Radar */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center', color: '#3498db' }}>
                🏢 {language === 'he' ? 'מפת רדאר עסקית' : 'Business Radar Map'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <svg width="400" height="400" viewBox="0 0 400 400">
                  <g dangerouslySetInnerHTML={{ __html: drawRadarChart(processedScores.business, 200, 200, 140) }} />
                </svg>
              </Box>
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#666' }}>
                {language === 'he' ? 'ממוצע עסקי:' : 'Business Average:'} <strong style={{ color: getScoreColor(processedScores.businessAverage) }}>
                  {processedScores.businessAverage.toFixed(1)}%
                </strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Radar */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center', color: '#9b59b6' }}>
                👤 {language === 'he' ? 'מפת רדאר אישית' : 'Personal Radar Map'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <svg width="400" height="400" viewBox="0 0 400 400">
                  <g dangerouslySetInnerHTML={{ __html: drawRadarChart(processedScores.personal, 200, 200, 140) }} />
                </svg>
              </Box>
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#666' }}>
                {language === 'he' ? 'ממוצע אישי:' : 'Personal Average:'} <strong style={{ color: getScoreColor(processedScores.personalAverage) }}>
                  {processedScores.personalAverage.toFixed(1)}%
                </strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RadarChartDashboard;
